import { 
  collection,
  query,
  where,
  getDocs,
  limit,
  orderBy
} from 'firebase/firestore';
import { db } from './config';

/**
 * Get all searchable sites with optional filters
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} - Result with success flag and data
 */
export const getSearchableSites = async (filters = {}) => {
  try {
    const searchableSitesRef = collection(db, 'searchable_sites');
    let q = query(searchableSitesRef);

    // Apply filters
    if (filters.name) {
      // Case-insensitive name search
      const nameFilter = filters.name.toLowerCase();
      q = query(q, where('name_lowercase', '>=', nameFilter), where('name_lowercase', '<=', nameFilter + '\uf8ff'));
    }

    if (filters.country) {
      // Case-insensitive country search
      const countryFilter = filters.country.toLowerCase();
      q = query(q, where('country_lowercase', '==', countryFilter));
    }

    if (filters.era) {
      q = query(q, where('era', '==', filters.era));
    }

    // Feature filtering - use siteLevelAllImageFeatureTags for site-level filtering
    if (filters.features && filters.features.length > 0) {
      if (filters.featureFilterLogic === 'OR' || filters.features.length === 1) {
        // For OR logic or single feature, use array-contains
        q = query(q, where('siteLevelAllImageFeatureTags', 'array-contains', filters.features[0]));
      } else {
        // For AND logic with multiple features, we need to use array-contains-any then filter client-side
        // or use multiple array-contains queries (Firestore limitation)
        q = query(q, where('siteLevelAllImageFeatureTags', 'array-contains', filters.features[0]));
      }
    }

    // Execute query
    const snapshot = await getDocs(q);
    let sites = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Additional client-side filtering for AND logic with multiple features
    if (filters.features && filters.features.length > 1 && filters.featureFilterLogic === 'AND') {
      sites = sites.filter(site => 
        filters.features.every(featureId => 
          site.siteLevelAllImageFeatureTags.includes(featureId)
        )
      );
    }

    // Additional client-side filtering for OR logic with multiple features (if needed)
    if (filters.features && filters.features.length > 1 && filters.featureFilterLogic === 'OR') {
      sites = sites.filter(site => 
        filters.features.some(featureId => 
          site.siteLevelAllImageFeatureTags.includes(featureId)
        )
      );
    }

    // Filter by countries if multiple specified
    if (filters.countries && filters.countries.length > 0) {
      sites = sites.filter(site => 
        filters.countries.includes(site.country)
      );
    }

    return {
      success: true,
      data: sites
    };
  } catch (error) {
    console.error('Error fetching searchable sites:', error);
    return { 
      success: false, 
      error: 'Failed to fetch sites',
      details: error.message 
    };
  }
};

/**
 * Get searchable sites by state/region
 * @param {string} state - State or region name
 * @returns {Promise<Object>} - Result with success flag and data
 */
export const getSearchableSitesByState = async (state) => {
  try {
    const searchableSitesRef = collection(db, 'searchable_sites');
    
    // Try exact match first, then partial match
    let q = query(searchableSitesRef, where('location.address', '>=', state), where('location.address', '<=', state + '\uf8ff'));
    
    let snapshot = await getDocs(q);
    let sites = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // If no exact matches, do a broader search
    if (sites.length === 0) {
      const allSitesQuery = query(searchableSitesRef);
      const allSnapshot = await getDocs(allSitesQuery);
      const searchTerm = state.toLowerCase();
      
      sites = allSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(site => 
          site.location?.address?.toLowerCase().includes(searchTerm)
        );
    }

    return {
      success: true,
      data: sites
    };
  } catch (error) {
    console.error('Error fetching searchable sites by state:', error);
    return { 
      success: false, 
      error: 'Failed to fetch sites by state',
      details: error.message 
    };
  }
};

/**
 * Search searchable sites by name
 * @param {string} name - Site name to search for
 * @returns {Promise<Object>} - Result with success flag and data
 */
export const searchSearchableSitesByName = async (name) => {
  try {
    const searchableSitesRef = collection(db, 'searchable_sites');
    const searchTerm = name.toLowerCase();
    
    // Use name_lowercase field for efficient searching
    const q = query(
      searchableSitesRef,
      where('name_lowercase', '>=', searchTerm),
      where('name_lowercase', '<=', searchTerm + '\uf8ff')
    );
    
    const snapshot = await getDocs(q);
    const sites = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      success: true,
      data: sites
    };
  } catch (error) {
    console.error('Error searching searchable sites by name:', error);
    return { 
      success: false, 
      error: 'Failed to search sites by name',
      details: error.message 
    };
  }
};

/**
 * Get searchable sites by specific features
 * @param {Array} featureIds - Array of standardized feature IDs
 * @param {string} logic - 'AND' or 'OR' logic for multiple features
 * @returns {Promise<Object>} - Result with success flag and data
 */
export const getSearchableSitesByFeatures = async (featureIds, logic = 'AND') => {
  try {
    if (!featureIds || featureIds.length === 0) {
      return await getSearchableSites();
    }

    const searchableSitesRef = collection(db, 'searchable_sites');
    
    // For single feature or OR logic, use array-contains
    let q = query(searchableSitesRef, where('siteLevelAllImageFeatureTags', 'array-contains', featureIds[0]));
    
    const snapshot = await getDocs(q);
    let sites = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Apply client-side filtering for complex logic
    if (featureIds.length > 1) {
      if (logic === 'AND') {
        sites = sites.filter(site => 
          featureIds.every(featureId => 
            site.siteLevelAllImageFeatureTags.includes(featureId)
          )
        );
      } else if (logic === 'OR') {
        sites = sites.filter(site => 
          featureIds.some(featureId => 
            site.siteLevelAllImageFeatureTags.includes(featureId)
          )
        );
      }
    }

    return {
      success: true,
      data: sites
    };
  } catch (error) {
    console.error('Error fetching searchable sites by features:', error);
    return { 
      success: false, 
      error: 'Failed to fetch sites by features',
      details: error.message 
    };
  }
};

/**
 * Get all unique feature tags from searchable sites (for filter options)
 * @returns {Promise<Object>} - Result with success flag and feature metadata
 */
export const getSearchableSiteFeatures = async () => {
  try {
    const searchableSitesRef = collection(db, 'searchable_sites');
    const snapshot = await getDocs(searchableSitesRef);
    
    const featureMap = new Map();
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (Array.isArray(data.siteLevelAllImageFeatureTags)) {
        data.siteLevelAllImageFeatureTags.forEach(featureId => {
          if (featureId && !featureMap.has(featureId)) {
            featureMap.set(featureId, {
              id: featureId,
              label: featureId, // Could be enhanced with proper label mapping
              count: 1
            });
          } else if (featureId) {
            const existing = featureMap.get(featureId);
            existing.count++;
          }
        });
      }
    });

    return {
      success: true,
      data: Object.fromEntries(featureMap)
    };
  } catch (error) {
    console.error('Error fetching searchable site features:', error);
    return { 
      success: false, 
      error: 'Failed to fetch site features',
      details: error.message 
    };
  }
};

/**
 * Get a single searchable site by ID
 * @param {string} siteId - Site ID
 * @returns {Promise<Object>} - Result with success flag and data
 */
export const getSearchableSiteById = async (siteId) => {
  try {
    const searchableSitesRef = collection(db, 'searchable_sites');
    const q = query(searchableSitesRef, where('originalSiteId', '==', siteId));
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return {
        success: false,
        error: 'Site not found'
      };
    }

    const siteData = {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data()
    };

    return {
      success: true,
      data: siteData
    };
  } catch (error) {
    console.error('Error fetching searchable site by ID:', error);
    return { 
      success: false, 
      error: 'Failed to fetch site',
      details: error.message 
    };
  }
};

/**
 * Get all sublocations for a specific site
 * @param {string} siteId - The ID of the parent site
 * @returns {Promise<Object>} - Result with success flag and data (array of sublocations)
 */
export const getSiteSublocations = async (siteId) => {
  if (!siteId) {
    return {
      success: false,
      error: 'Site ID is required to fetch sublocations',
      data: []
    };
  }
  try {
    const sublocationsRef = collection(db, 'site_sublocations');
    const q = query(sublocationsRef, where('siteId', '==', siteId));
    
    const snapshot = await getDocs(q);
    const sublocations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      success: true,
      data: sublocations
    };
  } catch (error) {
    console.error(`Error fetching sublocations for site ${siteId}:`, error);
    return {
      success: false,
      error: `Failed to fetch sublocations for site ${siteId}`,
      details: error.message,
      data: []
    };
  }
};
